import type { FC } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaStar } from "react-icons/fa";
import { LuCircleUserRound } from "react-icons/lu";
import Button from "../../../../../../components/button/button";
import Icon from "../../../../../../components/icon/icon";
import Input from "../../../../../../components/input/input";
import ToolbarDropdown from "../../../../../../components/toolbar-dropdown/toolbar-dropdown";
import { colours } from "../../../../../../styles/colours.constants";
import type { AuthorComment } from "../../../../../../types/comments.types";
import type { ChatStatuses } from "../chat-module.types";
import * as CSS from "./comment.styles";
import { useComment } from "./use-comment";

type CommentProps = {
	comment: AuthorComment;
	broadcastId: string;
	status: ChatStatuses;
	readOnly?: boolean;
	// Only used in readOnly mode, where "You" (the author, replying) doesn't
	// apply — falls back to "Author" if the name hasn't loaded yet.
	authorName?: string | null;
};

const Comment: FC<CommentProps> = ({
	comment,
	broadcastId,
	status,
	readOnly,
	authorName,
}) => {
	const {
		showReplyBox,
		setShowReplyBox,
		replyText,
		setReplyText,
		commentTimeAgo,
		replyTimeAgo,
		isMenuOpen,
		menuRef,
		menuOptions,
		handleToggleMenu,
		handleMenuItemSelected,
		handleReply,
		handleKeyDown,
	} = useComment({ comment, broadcastId, status, readOnly });

	return (
		<CSS.CommentWrapper $includeBottomBorder={!readOnly}>
			<CSS.Comment>
				<CSS.CommentCol>
					<CSS.InnerContainer>
						<Icon icon={LuCircleUserRound} size={24} color={colours.black} />
						<span>
							<CSS.UserName>
								{comment.userName} - {commentTimeAgo}
							</CSS.UserName>
							<CSS.Text>
								{comment.sharedToFeed && (
									<Icon
										icon={FaStar}
										color={colours.yellow}
										size={16}
										style={{ marginTop: -2 }}
									/>
								)}
								{comment.text}
							</CSS.Text>
						</span>
					</CSS.InnerContainer>
					{comment.reply && (
						<CSS.InnerContainer style={{ marginLeft: "2rem", marginTop: 10 }}>
							<Icon icon={LuCircleUserRound} size={24} color={colours.black} />
							<span>
								<CSS.UserName>
									{readOnly ? (authorName ?? "Author") : "You"} -{" "}
									{replyTimeAgo}
								</CSS.UserName>
								<CSS.Text>{comment.reply}</CSS.Text>
							</span>
						</CSS.InnerContainer>
					)}
				</CSS.CommentCol>
				<CSS.Actions>
					{menuOptions.length > 0 && (
						<CSS.MenuTrigger ref={menuRef}>
							<Icon
								icon={BsThreeDotsVertical}
								as="button"
								size={24}
								color={colours.greyDarker}
								onClick={handleToggleMenu}
							/>
							{isMenuOpen && (
								<ToolbarDropdown
									items={menuOptions}
									onMenuItemSelected={handleMenuItemSelected}
									align="right"
								/>
							)}
						</CSS.MenuTrigger>
					)}
				</CSS.Actions>
			</CSS.Comment>
			{showReplyBox && (
				<CSS.InputWrapper>
					<Input
						value={replyText}
						onChange={(e) => setReplyText(e.target.value)}
						placeholder={`Reply to ${comment.userName}`}
						onKeyDown={handleKeyDown}
					/>
					<Button $variant="secondary" onClick={() => setShowReplyBox(false)}>
						Cancel
					</Button>
					<Button
						disabled={replyText?.trim().length === 0}
						onClick={handleReply}
					>
						Reply
					</Button>
				</CSS.InputWrapper>
			)}
		</CSS.CommentWrapper>
	);
};

export default Comment;
